/**
 * Centralized definitions for Webflow entitlement keys and mappings used throughout the codebase.
 */

import {invert} from 'lodash';

/**
 * Webflow entitlement keys used to control feature access and limits across the codebase.
 */
export const ENTITLEMENT_KEYS = {
  ACCEPT_CREDIT_CARDS: 'acceptsCreditCard',
  ACCEPTS_INVOICE: 'acceptsInvoice',
  ALLOW_ADVANCED_PERMISSIONS: 'advancedPermissions',
  ALLOW_AEO_RECOMMENDATIONS: 'allowAeoRecommendations',
  ALLOW_AI_ASSISTANT_WRITE: 'allowAiAssistantWrite',
  ALLOW_ANALYZE_CORE: 'allowAnalyzeCore',
  ALLOW_ANALYZE_DATA_EXPORTS: 'allowAnalyzeDataExports',
  ALLOW_ASSET_STUDIO: 'allowAssetStudio', // feature-allow-asset-studio
  ALLOW_BASIC_USER_ROLES: 'allowBasicUserRoles',
  ALLOW_BRANCH_APPROVAL_WORKFLOW: 'allowBranchApprovalWorkflow',
  ALLOW_BRANDING: 'allowBranding',
  ALLOW_CERTIFIED_PARTNER_PROGRAM: 'allowCertifiedPartnerProgram', // feature-allow-certified-partner-program
  ALLOW_CLIENT_PAYMENTS: 'allowClientPayments',
  ALLOW_CLIENT_SEATS: 'allowClientSeats', // feature-allow-client-seats
  ALLOW_CLIENT_SEATS_FOR_SP_ONLY: 'allowClientSeatsForSpOnly', // feature-client-seats-for-sp-only
  ALLOW_CODE_COMPONENTS: 'allowCodeComponents',
  ALLOW_CUSTOM_CODE: 'allowCustomCode',
  ALLOW_CUSTOM_HEADERS: 'allowCustomHeaders',
  ALLOW_CUSTOM_STAGING: 'allowCustomStaging',
  ALLOW_DISABLE_SUBDOMAIN_INDEXING_BY_DEFAULT:
    'allowDisableSubdomainIndexingByDefault', // feature-allow-disable-subdomain-indexing-by-default
  ALLOW_EXPORT: 'allowExport',
  ALLOW_HIDE_BRANDING: 'allowHideBranding',
  ALLOW_LIMITED_DESIGNER_ROLE: 'allowLimitedDesignerRole',
  ALLOW_BRANCH_DESIGNER_ROLE: 'allowBranchDesignerRole',
  ALLOW_MARKDOWN_FOR_AGENTS: 'allowMarkdownForAgents', // feature-allow-markdown-for-agents
  ALLOW_MULTIPLE_WORKSPACE_USERS: 'allowMultipleWorkspaceUsers',
  ALLOW_PAGE_BRANCHING: 'allowPageBranching',
  ALLOW_PAGE_BRANCHING_AWARENESS: 'allowPageBranchingAwareness',
  ALLOW_PERMISSION_OVERRIDES: 'allowPermissionOverrides',
  // Granted by either a workspace or site add-on; enforce through getPrivateHostingAccess.
  ALLOW_PRIVATE_HOSTING: 'allowPrivateHosting',
  ALLOW_PRIVATE_STAGING: 'allowPrivateStaging',
  ALLOW_REQUIRE_ADDITIONAL_APPROVAL: 'allowRequireAdditionalApproval',
  ALLOW_SEAMLESS_SITE_TRANSFERS: 'allowSeamlessSiteTransfers', // feature-allow-seamless-site-transfers
  ALLOW_SITE_ACTIVITY_LOG: 'siteActivityLog',
  ALLOW_SITE_PASSWORDS: 'allowSitePasswords',
  ALLOW_SITE_ROLES: 'allowSiteRoles',
  ALLOW_SITE_SHOWCASING: 'allowSiteShowcasing',
  ALLOW_TRANSFERS: 'allowTransfers',
  ALLOW_UNIVERSITY_PRO: 'allowUniversityPro',
  ALLOW_WELL_KNOWN: 'allowWellKnown',
  ALLOW_WORKSPACE_ADMIN_APIS: 'allowWorkspaceAdminAPIs',
  ALLOW_WORKSPACE_AUDIT_LOG: 'allowWorkspaceAuditLog',
  ALLOW_WORKSPACE_ROLES: 'allowWorkspaceRoles',
  ANALYZE_SESSION_LIMIT: 'analyzeSessionsLimit',
  BILLING_PERMISSIONS: 'billingPermissions',
  CAN_BE_GUEST: 'canBeGuest',
  CAN_EXCEED_AI_CREDITS_LIMIT: 'canExceedAiCreditsLimit',
  // Enterprise hosts may opt agency guests into the host AI credit pool
  CAN_ALLOW_GUEST_TO_SPEND_HOST_CREDITS: 'canAllowGuestToSpendHostCredits', // feature-can-allow-guest-to-spend-host-credits
  CAN_EXCEED_MAX_ENTERPRISE_SITES: 'canExceedMaxEnterpriseSites',
  CAN_EXCEED_MAX_SEATS: 'canExceedMaxSeats',
  CAN_INVITE_GUESTS: 'canInviteGuests',
  EMAIL_SUPPORT: 'emailSupport',
  MAX_AGENCY_GUEST_MEMBERS: 'maxAgencyGuestMembers',
  MAX_COMMENTER_SEATS: 'maxCommenterSeats',
  ALLOW_DEVLINK_EXPORT: 'allowDevLinkExport',
  MANAGE_WORKSPACE_PLAN: 'manageWorkspacePlan',
  MAX_FULL_ACCESS_SEATS: 'maxFullAccessSeats',
  MAX_GUESTS: 'maxGuests',
  MAX_HOSTED_SITES: 'maxHostedSites',
  MAX_LIMITED_ACCESS_SEATS: 'maxLimitedAccessSeats',
  MAX_TEMPLATES: 'maxTemplates',
  MAX_UNHOSTED_SITES: 'maxUnhostedSites',
  PUBLISHING_PERMISSIONS: 'publishingPermissions',
  SEATS: 'maxSeats',
  SITE_SPECIFIC_ACCESS: 'siteSpecificAccess',
  ALLOW_301: 'allow301',
  ALLOW_ADVANCED_PUBLISH: 'allowAdvancedPublish',
  ALLOW_COLLABS: 'allowCollabs',
  ALLOW_CUSTOM_404: 'allowCustom404',
  ALLOW_CUSTOM_FORMS: 'allowCustomForms',
  ALLOW_CUSTOM_SITE_HEADER: 'allowCustomSiteHeader',
  ALLOW_CUSTOM_SSL_CERT: 'allowCustomSslCert',
  ALLOW_DOCUMENT_UPLOADS: 'allowDocumentUploads',
  ALLOW_ECOMMERCE: 'allowEcommerce',
  ALLOW_EXTRA_COLLABS: 'allowExtraCollabs',
  ALLOW_FASTER_SEO_INDEXING: 'allowFasterSeoIndexing',
  ALLOW_FORM_FILE_UPLOAD: 'allowFormFileUpload',
  ALLOW_FULL_SEAT_LIMITED_ROLE_FALLBACK: 'allowFullSeatLimitedRoleFallback',
  ALLOW_GLOBAL_CDN: 'allowGlobalCDN',
  ALLOW_HIDE_ECOMM_EMAIL_BRANDING: 'allowHideEcommEmailBranding',
  ALLOW_HIDE_USYS_EMAIL_BRANDING: 'allowHideUsysEmailBranding',
  ALLOW_HUBSPOT_MARKETING: 'allowHubspotMarketing',
  ALLOW_MARKETO_MARKETING: 'allowMarketoMarketing',
  ALLOW_OPTIMIZE_AI: 'allowOptimizeAI',
  ALLOW_OPTIMIZE_CORE: 'allowOptimizeCore',
  ALLOW_OPTIMIZE_ENTERPRISE_FEATURES: 'allowOptimizeEnterpriseFeatures',
  ALLOW_OPTIMIZE_ENHANCED_MATCH: 'allowOptimizeEnhancedMatch',
  ALLOW_OPTIMIZE_INTEGRATIONS: 'allowOptimizeIntegrations',
  ALLOW_OPTIMIZE_OFF_SITE_CUSTOM_METRIC: 'allowOptimizeOffSiteCustomMetric', // feature-allow-optimize-off-site-custom-metric
  ALLOW_OPTIMIZE_OFFLINE_CONVERSION_TOKEN:
    'allowOptimizeOfflineConversionToken', // feature-allow-optimize-offline-conversion-token
  ALLOW_PAGE_PASSWORDS: 'allowPagePasswords',
  ALLOW_PROJECT_LINK: 'allowProjectLink',
  ALLOW_PUBLISH_DOMAIN: 'allowPublishDomain',
  ALLOW_PUBLISHING_WORKFLOWS: 'allowPublishingWorkflows',
  ALLOW_RATE_LIMIT_OVERRIDE: 'allowRateLimitOverride',
  ALLOW_SCHEDULED_SIP: 'allowScheduledSIP',
  ALLOW_SEO: 'allowSEO',
  ALLOW_SITEMAP_CONTROLS: 'allowSitemapControls',
  ALLOW_SINGLE_SIGN_ON: 'allowSingleSignOn',
  ALLOW_SINGLE_PAGE_PUBLISHING: 'allowSinglePagePublishing',
  ALLOW_SITE_SEARCH: 'allowSiteSearch',
  ALLOW_TYPEKIT: 'allowTypekit',
  ALLOW_UNLIMITED_CDN_BANDWIDTH: 'allowUnlimitedCDNBandwidth',
  API_RATE_LIMIT: 'rateLimit',
  CAN_USE_ADVANCED_LOCALIZATION: 'canUseAdvancedLocalization',
  CAN_USE_LOCALIZATION_BROWSER_DETECTION: 'canUseLocalizationBrowserDetection',
  CAN_USE_LOCALIZATION_GEOLOCATION_DETECTION:
    'canUseLocalizationGeolocationDetection',
  CAN_LOCALIZE_CMS: 'canLocalizeCMS',
  CAN_LOCALIZE_STATIC_CONTENT: 'canLocalizeStaticContent',
  CAN_LOCALIZE_SEO: 'canLocalizeSEO',
  CAN_LOCALIZE_URLS: 'canLocalizeURLs',
  CAN_LOCALIZE_ASSETS: 'canLocalizeAssets',
  CAN_LOCALIZE_ELEMENT_VISIBILITY: 'canLocalizeElementVisibility',
  CAN_LOCALIZE_IMAGE_ELEMENTS: 'canLocalizeImageElements',
  CAN_PUBLISH_LOCALES: 'canPublishLocales',
  CDN_BANDWIDTH_LIMIT: 'cdnBandwidthLimit',
  FORM_SUBMISSIONS: 'warnLimitForms',
  FREE_CMS_ITEMS: 'freeCMSItems',
  FREE_COLLABS: 'freeCollabs',
  FREE_ECOM_ITEMS: 'freeEcomItems',
  GIVES_LEGACY_BRANDING: 'givesLegacyBranding',
  HAS_CUSTOMER_SUCCESS_MANAGER: 'hasCustomerSuccessManager',
  LOCALES: 'maxLocales',
  LOCALIZATION_WORDS_TRANSLATED: 'localizationWordsTranslated',
  MAX_COLLECTIONS: 'maxCollections',
  MAX_COLLECTION_FIELDS: 'maxCollectionFields',
  COLLECTION_ITEM_REFS: 'maxCollectionItemRefs',
  MAX_CUSTOM_SITE_ROLES: 'maxCustomSiteRoles',
  MAX_FLOWS: 'maxFlows',
  MAX_FLOW_EXECUTIONS: 'maxFlowExecutions',
  MAX_OPTIMIZATIONS: 'maxOptimizations',
  MAX_PAGE_TEMPLATES: 'maxPageTemplates',
  MAX_PAGES: 'maxPages',
  MAX_VARIABLE_MODES: 'maxVariableModes',
  MONTHLY_VISITS: 'warnMonthlyViews',
  OPTIMIZE_PAGE_VIEWS_LIMIT: 'optimizePageViewsLimit',
  SEARCH_INDEX_PERIOD_AUTO: 'searchIndexPeriodAuto',
  SEARCH_INDEX_PERIOD_MANUAL: 'searchIndexPeriodManual',
  TRANSACTION_FEE: 'transactionFee',
  UNCAPPED_API: 'uncappedAPI',
  USYS_MAX_USERS: 'usysMaxUsers',
  VISIBLE_BACKUPS: 'visibleBackups',
  MAX_WORKSPACE_LIBRARIES: 'maxWorkspaceLibraries',
  ALLOW_WORKSPACE_LIBRARY_AUTO_INSTALL: 'allowWorkspaceLibraryAutoInstall',
  MAX_AIDA_PROMPTS: 'maxAidaPrompts',
  EXCLUDE_FROM_OVERAGES: 'excludeFromOverages',
  EXCLUDE_FROM_PNP_31_UPGRADE: 'excludeFromPnp31Upgrade',
  ALLOW_HIDE_PROFILE_CREATION_BANNER: 'allowHideProfileCreationBanner',
  ALLOW_DRAFT_PAGES_PER_LOCAL: 'allowDraftPagesPerLocale',
  ALLOW_ENTERPRISE_BILLING_EXPERIENCE: 'allowEnterpriseBillingExperience',
  ALLOW_SHORT_CLOUDFLARE_CACHE_CONTROL_LENGTH:
    'allowShortCloudflareCacheControlLength', // feature-allow-short-cloudflare-cache-control-length
  // Enterprise entitlements
  ALLOW_CUSTOM_SECURITY_QUESTIONNAIRES: 'allowCustomSecurityQuestionnaires', // feature-allow-custom-security-questionnaires
  ALLOW_DESIGN_APPROVALS: 'allowDesignApprovals', // feature-allow-design-approvals
  ALLOW_SITE_ADMIN_APIS: 'allowSiteAdminAPIs', // feature-allow-site-admin-apis
  ALLOW_BRANCH_STAGING: 'allowBranchStaging', // feature-branch-staging
  ALLOW_ENTERPRISE_SUPPORT_QUEUE: 'allowEnterpriseSupportQueue', // feature-allow-enterprise-support-queue
  MAJOR_INCIDENT_RESPONSE_TIME: 'majorIncidentResponseTime', // feature-major-incident-response-time
  MINOR_INCIDENT_RESPONSE_TIME: 'minorIncidentResponseTime', // feature-minor-incident-response-time
  WORKSPACE_ENTERPRISE_ANALYZE_SITE_LIMIT:
    'workspaceEnterpriseAnalyzeSiteLimit', // feature-workspace-enterprise-analyze-site-limit
  WORKSPACE_ENTERPRISE_OPTIMIZE_SITE_LIMIT:
    'workspaceEnterpriseOptimizeSiteLimit', // feature-workspace-enterprise-optimize-site-limit
  WORKSPACE_POOLED_CMS_ITEMS: 'workspacePooledCmsItems', // feature-workspace-pooled-cms-items
  WORKSPACE_POOLED_LOCALES: 'workspacePooledLocales', // feature-workspace-pooled-locales
  ALLOW_ACCESS_TO_ENTERPRISE_WHISTIC_PROFILE:
    'allowAccessToEnterpriseWhisticProfile', // feature-allow-access-to-enterprise-whistic-profile
  ALLOW_CMS_CONTENT_DELIVERY_API: 'allowCmsContentDeliveryApi', // feature-allow-cms-content-delivery-api
  ALLOW_ENTERPRISE_CLOUD_APPS: 'allowEnterpriseCloudApps', // feature-allow-enterprise-cloud-apps
  ALLOW_ENTERPRISE_SELF_PROVISIONING_API: 'allowEnterpriseSelfProvisioningApi', // feature-allow-enterprise-self-provisioning-api
  ALLOW_ENTERPRISE_SELF_PROVISIONING_UI: 'allowEnterpriseSelfProvisioningUi', // feature-allow-enterprise-self-provisioning-ui
  ENTERPRISE_SITE_LIMIT: 'enterpriseSiteLimit', // feature-enterprise-site-limit
  ENTERPRISE_CLOUD_APP_LIMIT: 'enterpriseCloudAppLimit', // feature-enterprise-cloud-app-limit
  HOSTING_UPTIME: 'hostingUptime', // feature-hosting-uptime
  ALLOW_AI_ENTERPRISE_FEATURES: 'allowAiEnterpriseFeatures', // feature-allow-ai-enterprise-features
  TOTAL_RUM_SESSION_SAMPLE_RATE: 'totalRumSessionSampleRate',
  MAX_ORION_APP_MESSAGES_PROMPT: 'maxOrionAppMessagesPrompt', // feature-max-orion-app-messages-prompt
  MAX_ORION_VM_RUNTIME_MINUTES: 'maxOrionVmRuntimeMinutes', // feature-max-orion-vm-runtime-minutes
  AIA_CREDITS: 'aiaCredits', // feature-aia-credits
  APP_GEN_CREDITS: 'appGenCredits', // feature-app-gen-credits
  GENERATE_COPY_CREDITS: 'generateCopyCredits', // feature-generate-copy-credits
  GENERATE_CMS_COLLECTION_ITEMS_CREDITS: 'generateCmsCollectionItemsCredits', // feature-generate-cms-collection-items-credits
  IMPROVE_SEO_AND_AEO_CREDITS: 'improveSeoAndAeoCredits', // feature-improve-seo-and-aeo-credits
  AEO_RECOMMENDATIONS_CREDITS: 'aeoRecommendationsCredits', // feature-aeo-recommendations-credits
  AI_WORKFLOWS_CREDITS: 'aiWorkflowsCredits', // feature-ai-workflows-credits
  CAMPAIGN_CREDITS: 'campaignCredits', // feature-campaign-credits
  ALLOW_CAMPAIGN_SURFACING: 'allowCampaignSurfacing', // feature-allow-campaign-surfacing
  ALLOW_CAMPAIGN_CORE: 'allowCampaignCore', // feature-allow-campaign-core
  MAX_UNHOSTED_CAMPAIGN_HUBS: 'maxUnhostedCampaignHubs', // feature-max-unhosted-campaign-hubs
  MAX_HOSTED_CAMPAIGN_HUBS: 'maxHostedCampaignHubs', // feature-max-hosted-campaign-hubs
  CAMPAIGN_PAGE_VIEWS_LIMIT: 'campaignPageViewsLimit', // feature-campaign-page-views-limit
  SLACK_CREDITS: 'slackCredits', // feature-slack-credits
  AGENT_INSTRUCTIONS_CREDITS: 'agentInstructionsCredits', // feature-agent-instructions-credits
  LOCALIZATION_CREDITS: 'localizationCredits', // feature-localization-credits
  WF_CLOUD_PROJECT_LIMIT: 'webflowCloudProjectLimit', // feature-wf-cloud-project-limit
  WF_CLOUD_CPU_TIME_LIMIT: 'wfCloudCpuTimeLimit', // feature-wf-cloud-cpu-time-limit
  WF_CLOUD_REQUEST_LIMIT: 'wfCloudRequestLimit', // feature-wf-cloud-requests-count-limit
  WF_CLOUD_STORAGE: 'wfCloudStorage', // feature-cloud-storage
  STATSIG_ENTERPRISE_WORKSPACE_IDENTIFIER:
    'statsigEnterpriseWorkspaceIdentifier', // feature-allow-statsig-enterprise-workspace-identifier
  STATSIG_ENTERPRISE_SITE_IDENTIFIER: 'statsigEnterpriseSiteIdentifier', // feature-allow-statsig-enterprise-site-identifier
  WF_CLOUD_LOG_LIMIT: 'wfCloudLogLimit', // feature-wf-cloud-log-limit
  ALLOW_GRANULAR_ACCESS: 'allowGranularAccess', // feature-allow-granular-access
  ALLOW_SHARING_PERMISSIONS: 'allowSharingPermissions', // feature-sharing-permissions
  CLOUDFLARE_DNS_UPDATE_DEADLINE: 'cloudflareDNSUpdateDeadline',
  EXCLUDE_FROM_RENEWAL_NOTIFICATIONS: 'excludeFromRenewalNotifications', // feature-exclude-from-renewal-notifications
  CLIENT_SEATS_COUNT_FOR_SITE_GLOBAL: 'clientSeatsForSiteLimitGlobal', // feature-client-seats-count-for-site-global
  CLIENT_SEATS_COUNT_FOR_SITE_PAID: 'clientSeatsForSiteLimitPaid', // feature-client-seats-count-for-site-paid
  ALLOW_LIBRARY_PUSH_UPDATES: 'allowLibraryPushUpdates', // feature-allow-library-push-updates
  INCLUDE_SITE_ENT_PUBLISHING_METRICS: 'includeSiteEntPublishingMetrics', // feature-include-site-ent-publishing-metrics
  ALLOW_CMS_ENTERPRISE: 'allowCmsEnterprise', // feature-allow-cms-enterprise
  PREVENT_WORKSPACE_DELETION: 'preventWorkspaceDeletion', // feature-prevent-workspace-deletion
  PREVENT_WORKSPACE_ACCESS_REQUESTS: 'preventWorkspaceAccessRequests', // feature-prevent-workspace-access-requests
  MAX_FILE_SUBMISSION_UPLOAD_STORAGE_LIMIT:
    'maxFileSubmissionUploadStorageLimit', // feature-max-file-submission-upload-storage-limit
  ALLOW_SITE_ENTERPRISE_OPTIMIZE_CORE: 'allowSiteEnterpriseOptimizeCore', // feature-allow-site-enterprise-optimize-core
  ALLOW_SITE_ENTERPRISE_ANALYZE_CORE: 'allowSiteEnterpriseAnalyzeCore', // feature-allow-site-enterprise-analyze-core
  ALLOW_SITE_ENTERPRISE_AEO_ANALYTICS: 'allowSiteEnterpriseAeoAnalytics', // feature-allow-site-enterprise-aeo-analytics
  ALLOW_BULK_USER_MANAGEMENT: 'allowBulkUserManagement', // feature-allow-bulk-user-management
  ALLOW_WAF: 'allowWaf', // feature-allow-waf
  // AEO Analytics entitlements
  ALLOW_AEO_ANALYTICS: 'allowAeoAnalytics', // feature-allow-aeo-analytics
  AEO_ANALYTICS_PROMPT_LIMIT: 'aeoAnalyticsPromptLimit', // feature-aeo-analytics-prompt-limit
  AEO_ANALYTICS_PROMPT_LLM_LIMIT: 'aeoAnalyticsPromptLlmLimit', // feature-aeo-analytics-prompt-llm-limit
  WORKSPACE_POOLED_AEO_ANALYTICS_PROMPT_LIMIT:
    'workspacePooledAeoAnalyticsPromptLimit', // feature-workspace-pooled-aeo-analytics-prompt-limit
} as const;

/*
 * This is a mapping of site entitlement to workspace pooled entitlement.
 * This is used to determine which entitlements are pooled across sites.
 * Site entitlement is the key and the workspace pooled entitlement is the value.
 * site entitlement can be a boolean entitlement or a numeric entitlement.
 * workspace pooled entitlement is always a numeric entitlement.
 * when site entitlement is a boolean entitlement, the incremental value is always 1.
 */
export const SITE_WORKSPACE_POOLED_ENTITLEMENT_MAPPING: Record<string, string> =
  {
    [ENTITLEMENT_KEYS.FREE_CMS_ITEMS]:
      ENTITLEMENT_KEYS.WORKSPACE_POOLED_CMS_ITEMS,
    [ENTITLEMENT_KEYS.LOCALES]: ENTITLEMENT_KEYS.WORKSPACE_POOLED_LOCALES,
    [ENTITLEMENT_KEYS.ALLOW_OPTIMIZE_CORE]:
      ENTITLEMENT_KEYS.WORKSPACE_ENTERPRISE_OPTIMIZE_SITE_LIMIT,
    [ENTITLEMENT_KEYS.ALLOW_ANALYZE_CORE]:
      ENTITLEMENT_KEYS.WORKSPACE_ENTERPRISE_ANALYZE_SITE_LIMIT,
    [ENTITLEMENT_KEYS.AEO_ANALYTICS_PROMPT_LIMIT]:
      ENTITLEMENT_KEYS.WORKSPACE_POOLED_AEO_ANALYTICS_PROMPT_LIMIT,
  } as const;

/**
 * Pre-computed inverted map for performance optimization.
 */
export const INVERTED_ENTITLEMENT_KEYS = invert({
  ...ENTITLEMENT_KEYS,
});

/**
 * Entitlements that can be granted as promotional entitlements in admin
 */
export const ADMIN_PROMOTIONAL_ENTITLEMENT_ALLOWLIST = [
  ENTITLEMENT_KEYS.ALLOW_SINGLE_PAGE_PUBLISHING,
] as const;
